// A small world, for exercising the parts of the contract real data cannot.
//
// Production has no computed features, no correction chain worth paging across,
// and no way to insert a row mid-page without writing to it. Those are exactly
// the cases the as-of and changelog rules exist for, so they get a fixture.
//
// Deliberately tiny and deliberately awkward: one player on a team with one
// fixture, one forecast that was RE-ISSUED (so there is a real correction to
// ask about), one teammate who did not practise, and one fact observed after
// everything else so `asOf` has something to exclude.

import type { Attribution, Entity } from './store';
import type { SimFact, SimWorld } from './sim-store';

export const NFLVERSE: Attribution = {
  sourceId: 'nflverse',
  name: 'nflverse',
  license: 'CC-BY-4.0',
  text: 'Data from nflverse (nflverse.com), licensed CC BY 4.0.',
  url: 'https://creativecommons.org/licenses/by/4.0/',
};

export const NWS: Attribution = {
  sourceId: 'nws',
  name: 'National Weather Service',
  license: 'US Government work, 17 U.S.C. §105 — public domain',
  text: 'Weather data from the National Weather Service (weather.gov).',
  url: 'https://www.weather.gov/documentation/services-web-api',
};

const source = (a: Attribution) => ({
  id: a.sourceId,
  name: a.name,
  license: a.license,
  attribution: a.text,
});

/** Before anything was observed. The as-of checks need an instant with no data. */
export const BEFORE_ANYTHING = '2026-09-01T00:00:00.000Z';
const T1 = '2026-09-10T09:00:00.000Z';
const T2 = '2026-09-12T09:00:00.000Z';
/** Between the first forecast and its re-issue. */
export const BETWEEN = '2026-09-12T12:00:00.000Z';
const T3 = '2026-09-13T09:00:00.000Z';
export const AFTER_EVERYTHING = '2026-09-14T00:00:00.000Z';

export const PLAYER = 'ent-player-1';
export const TEAMMATE = 'ent-player-2';
export const TEAM = 'ent-team-zz';
export const CONTEST = 'ent-contest-1';
export const ORIGINAL_FORECAST = 'fact-forecast-1';
export const REVISED_FORECAST = 'fact-forecast-2';

const entities: Entity[] = [
  {
    id: PLAYER,
    type: 'person',
    sport: 'football',
    league: 'nfl',
    display: 'Alpha Quarterback',
    keys: { 'nflverse-player': '00-0000001', gsis: '00-0000001' },
  },
  {
    id: TEAMMATE,
    type: 'person',
    sport: 'football',
    league: 'nfl',
    display: 'Bravo Tackle',
    keys: { 'nflverse-player': '00-0000002' },
  },
  {
    id: TEAM,
    type: 'team',
    sport: 'football',
    league: 'nfl',
    display: 'ZZA',
    keys: { 'nflverse-team': 'ZZA' },
  },
  {
    id: CONTEST,
    type: 'contest',
    sport: 'football',
    league: 'nfl',
    display: 'ZZB at ZZA, 2026 week 1',
    keys: { 'nflverse-game': '2026_01_ZZB_ZZA' },
  },
];

const fact = (
  f: Partial<SimFact> & Pick<SimFact, 'id' | 'kind' | 'payload' | 'refs'>,
): SimFact => ({
  validFrom: T1,
  validTo: null,
  observedAt: T1,
  source: source(NFLVERSE),
  confidence: 'official',
  supersededBy: null,
  supersedes: null,
  sport: 'football',
  league: 'nfl',
  season: 2026,
  period: 1,
  ...f,
});

const facts: SimFact[] = [
  fact({
    id: 'fact-profile-1',
    kind: 'person.profile',
    season: null,
    period: null,
    payload: {
      personKey: '00-0000001',
      gsisId: '00-0000001',
      displayName: 'Alpha Quarterback',
      firstName: 'Alpha',
      lastName: 'Quarterback',
      shortName: 'A.Quarterback',
      knownAs: 'Alpha',
      suffix: null,
      position: 'QB',
      positionGroup: 'QB',
      status: 'ACT',
      statusDescription: 'Active',
      birthDate: '1995-01-01',
      heightInches: 75,
      weightPounds: 225,
      college: 'State',
      // THESE FOUR WERE MISSING UNTIL 2026-09-20, and the payload therefore
      // did not satisfy the schema registered for `person.profile`. Nothing
      // noticed for as long as the fixture existed, because the SQL store is
      // the only one that validates a payload on the way out (`toFact` in
      // `dossier.ts`) and the SQL store had never been run against this
      // fixture — which is the whole of AUDIT-2026-09-19 F16. `store:verify`
      // found it on its first run.
      collegeConference: 'Fixture Conference',
      rookieSeason: 2017,
      lastSeason: null,
      headshot: null,
      draftYear: 2017,
      draftRound: 1,
      draftPick: 10,
      draftTeam: 'ZZA',
      latestTeam: 'ZZA',
      yearsExperience: 9,
      jerseyNumber: 1,
    },
    refs: [{ entityId: PLAYER, role: 'subject' }],
  }),

  fact({
    id: 'fact-roster-1',
    kind: 'roster.membership',
    payload: {
      personKey: '00-0000001',
      season: 2026,
      period: 1,
      team: 'ZZA',
      status: 'ACT',
      statusDetail: 'A01',
      position: 'QB',
      depthPosition: 'QB',
      jerseyNumber: 1,
      yearsExperience: 9,
      gameType: 'REG',
    },
    refs: [{ entityId: PLAYER, role: 'subject' }],
  }),

  fact({
    id: 'fact-roster-2',
    kind: 'roster.membership',
    payload: {
      personKey: '00-0000002',
      season: 2026,
      period: 1,
      team: 'ZZA',
      status: 'ACT',
      statusDetail: 'A01',
      position: 'T',
      depthPosition: 'LT',
      jerseyNumber: 70,
      yearsExperience: 4,
      gameType: 'REG',
    },
    refs: [{ entityId: TEAMMATE, role: 'subject' }],
  }),

  // The teammate who did not practise — the signal the directory drops.
  fact({
    id: 'fact-injury-2',
    kind: 'injury.report',
    observedAt: T2,
    payload: {
      personKey: '00-0000002',
      season: 2026,
      period: 1,
      team: 'ZZA',
      position: 'T',
      seasonType: 'REG',
      gameType: 'REG',
      reportStatus: null,
      reportInjury: null,
      practiceStatus: 'Did Not Participate In Practice',
      practiceInjury: 'Back',
      practiceSecondaryInjury: null,
    },
    refs: [{ entityId: TEAMMATE, role: 'subject' }],
    topics: ['injury'],
  }),

  fact({
    id: 'fact-contest-1',
    kind: 'contest.scheduled',
    observedAt: T1,
    payload: {
      gameId: '2026_01_ZZB_ZZA',
      season: 2026,
      period: 1,
      gameType: 'REG',
      kickoff: '2026-09-15T00:15:00.000Z',
      localDate: '2026-09-14',
      localTime: '20:15',
      weekday: 'Monday',
      sourceZone: 'America/New_York',
      home: 'ZZA',
      away: 'ZZB',
      location: 'Home',
      divisional: false,
      venue: 'ZZA00',
      venueName: 'Alpha Field',
      roof: 'outdoors',
      surface: 'grass',
      indoors: false,
      temp: null,
      wind: null,
      homeRest: 7,
      awayRest: 7,
      spreadLine: -2.5,
      totalLine: 43.5,
      homeMoneyline: -140,
      awayMoneyline: 120,
      referee: null,
      // Missing for the same reason as the four above.
      homeCoach: null,
      awayCoach: null,
    },
    refs: [
      { entityId: CONTEST, role: 'subject' },
      { entityId: TEAM, role: 'participant' },
    ],
  }),

  // A forecast, and the re-issue that corrects it. This pair is the only
  // supersession chain in the fixture and it is what checks 5, 6 and 16 read.
  fact({
    id: ORIGINAL_FORECAST,
    kind: 'forecast.kickoff',
    observedAt: T2,
    validFrom: T2,
    source: source(NWS),
    confidence: 'estimated',
    payload: {
      gameId: '2026_01_ZZB_ZZA',
      season: 2026,
      period: 1,
      venueId: 'ZZA00',
      windowStart: '2026-09-14T19:00:00-05:00',
      windowEnd: '2026-09-14T20:00:00-05:00',
      issuedAt: T2,
      temperatureF: 74,
      windMph: 8,
      windGustMph: null,
      windDirection: 'S',
      precipitationChance: 10,
      relativeHumidity: 60,
      dewpointF: 58,
      shortForecast: 'Partly Cloudy',
    },
    refs: [
      { entityId: CONTEST, role: 'subject' },
      { entityId: TEAM, role: 'participant' },
    ],
    topics: ['weather'],
  }),

  fact({
    id: REVISED_FORECAST,
    kind: 'forecast.kickoff',
    observedAt: T3,
    validFrom: T3,
    supersedes: ORIGINAL_FORECAST,
    source: source(NWS),
    confidence: 'estimated',
    payload: {
      gameId: '2026_01_ZZB_ZZA',
      season: 2026,
      period: 1,
      venueId: 'ZZA00',
      windowStart: '2026-09-14T19:00:00-05:00',
      windowEnd: '2026-09-14T20:00:00-05:00',
      issuedAt: T3,
      temperatureF: 61,
      windMph: 22,
      windGustMph: 30,
      windDirection: 'NW',
      precipitationChance: 70,
      relativeHumidity: 85,
      dewpointF: 57,
      shortForecast: 'Rain Likely',
    },
    refs: [
      { entityId: CONTEST, role: 'subject' },
      { entityId: TEAM, role: 'participant' },
    ],
    topics: ['weather'],
  }),
];

export function simWorld(): SimWorld {
  // A deep copy per call, so a test that mutates cannot reach the next one.
  return {
    entities: structuredClone(entities),
    facts: structuredClone(facts),
    attribution: [NFLVERSE, NWS],
    features: {},
  };
}
